import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { ArrowDown } from "lucide-react";
import heroImage from "@/assets/landing/hero-desert-house.jpg";
import { selectedProjects } from "@/data/projects";
import ConsultationForm from "@/components/landing/ConsultationForm";

const services = [
  {
    number: "01",
    title: "DESERT LANDSCAPE DESIGN",
    description:
      "Site planning, planting concepts, composition and material direction.",
  },
  {
    number: "02",
    title: "INSTALLATION",
    description:
      "Specimen planting, containers, irrigation, lighting, stone and final styling.",
  },
  {
    number: "03",
    title: "EXTERIOR EDITS",
    description: "Focused upgrades to the key exterior areas of the property.",
  },
  {
    number: "04",
    title: "PLANT AUTOPILOT",
    description: "Private Landscape Stewardship.",
  },
];

const principles = [
  "Architecture",
  "Sightlines",
  "Light",
  "Privacy",
  "Outdoor Use",
  "Desert Conditions",
];

const levels = [
  {
    name: "DESERT EDIT",
    description:
      "Focused transformation of one or more key exterior spaces.",
  },
  {
    name: "SIGNATURE",
    description: "Comprehensive redesign of the major outdoor areas.",
  },
  {
    name: "COMPLETE PROPERTY",
    description:
      "Full-property exterior design, installation and stewardship planning.",
  },
];

const Index: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-desert-cream text-desert-ink antialiased">
      <Helmet>
        <title>Plant Autopilot | Palm Springs Exterior Design</title>
        <meta
          name="description"
          content="Landscape design, installation and private landscape stewardship for Palm Springs homes. Desert exteriors, designed to belong."
        />
        <link rel="canonical" href="https://plantautopilot.com/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: "Plant Autopilot",
            description:
              "Landscape design, installation and private landscape stewardship for Palm Springs homes.",
            areaServed: "Palm Springs, CA",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Palm Springs",
              addressRegion: "CA",
              addressCountry: "US",
            },
          })}
        </script>
      </Helmet>

      {/* Header */}
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
          scrolled
            ? "bg-desert-cream/95 backdrop-blur-sm border-b border-desert-stone/40"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <a
            href="#top"
            className={`text-sm font-medium uppercase tracking-[0.25em] transition-colors ${
              scrolled ? "text-desert-ink" : "text-desert-cream"
            }`}
          >
            Plant Autopilot
          </a>
          <div className="flex items-center gap-8">
            <span
              className={`hidden md:block text-xs uppercase tracking-[0.2em] transition-colors ${
                scrolled ? "text-desert-charcoal/60" : "text-desert-cream/70"
              }`}
            >
              Palm Springs, CA
            </span>
            <a
              href="#consultation"
              className={`text-xs uppercase tracking-[0.2em] border-b pb-1 transition-colors ${
                scrolled
                  ? "text-desert-ink border-desert-ink hover:text-desert-olive-deep hover:border-desert-olive-deep"
                  : "text-desert-cream border-desert-cream/70 hover:border-desert-cream"
              }`}
            >
              Request a Consultation
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section id="top" className="relative h-[100svh] min-h-[600px]">
        <img
          src={heroImage}
          alt="Mid-century modern Palm Springs home at golden hour set within a sculptural desert landscape of agaves, boulders and olive trees"
          className="absolute inset-0 w-full h-full object-cover"
          width={1920}
          height={1080}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-desert-ink/70 via-desert-ink/20 to-desert-ink/10" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-10 flex flex-col justify-end pb-20 md:pb-28">
          <h1 className="font-display text-desert-cream text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] max-w-4xl">
            DESERT EXTERIORS,
            <br />
            DESIGNED TO BELONG.
          </h1>
          <p className="mt-6 text-desert-cream/85 text-base md:text-lg max-w-xl leading-relaxed">
            Landscape design, planting, lighting and private landscape
            stewardship for Palm Springs homes.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              href="#consultation"
              className="inline-flex items-center justify-center bg-desert-cream text-desert-ink px-10 py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-desert-sand transition-colors"
            >
              Request a Property Consultation
            </a>
            <a
              href="#work"
              className="inline-flex items-center justify-center border border-desert-cream/60 text-desert-cream px-10 py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-desert-cream/10 transition-colors"
            >
              View Our Work
            </a>
          </div>
        </div>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-desert-cream/60">
          <ArrowDown className="w-5 h-5 animate-bounce" aria-hidden="true" />
        </div>
      </section>

      {/* Positioning */}
      <section className="py-24 md:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.25em] text-desert-olive-deep mb-8">
              The Practice
            </p>
            <h2 className="font-display text-3xl md:text-5xl leading-[1.15] text-desert-ink">
              We design the exterior life of the house.
            </h2>
            <p className="mt-8 text-lg text-desert-charcoal/80 leading-relaxed">
              Every project begins with the architecture — the sightlines from
              inside the rooms, the path of the light, the need for privacy,
              the way you actually live outdoors, and what the desert will
              allow. The plants come later.
            </p>
          </div>
          <div className="mt-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-desert-stone/40 border border-desert-stone/40">
            {principles.map((p) => (
              <div
                key={p}
                className="bg-desert-cream px-6 py-8 text-center"
              >
                <span className="text-xs uppercase tracking-[0.2em] text-desert-olive-deep">
                  {p}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-24 md:py-32 bg-desert-sand">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="text-xs uppercase tracking-[0.25em] text-desert-olive-deep mb-14">
            Services
          </p>
          <div className="divide-y divide-desert-stone/40 border-y border-desert-stone/40">
            {services.map((s) => (
              <div
                key={s.number}
                className="grid grid-cols-12 gap-4 py-8 md:py-10 items-baseline"
              >
                <span className="col-span-2 md:col-span-1 text-sm text-desert-stone font-light">
                  {s.number}
                </span>
                <h3 className="col-span-10 md:col-span-5 font-display text-xl md:text-2xl text-desert-ink">
                  {s.title}
                </h3>
                <p className="col-span-12 md:col-span-6 text-desert-charcoal/75 leading-relaxed md:text-right">
                  {s.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Levels */}
      <section id="levels" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-desert-olive-deep mb-6">
                Ways of Working
              </p>
              <h2 className="font-display text-3xl md:text-4xl text-desert-ink">
                Three levels of engagement.
              </h2>
            </div>
            <p className="text-sm text-desert-charcoal/60 max-w-xs leading-relaxed">
              Investment ranges are discussed at consultation and quoted per
              property.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 border border-desert-stone/40 divide-y md:divide-y-0 md:divide-x divide-desert-stone/40">
            {levels.map((l, i) => (
              <div key={l.name} className="p-10 md:p-12">
                <span className="text-sm text-desert-stone font-light">
                  0{i + 1}
                </span>
                <h3 className="mt-6 font-display text-xl text-desert-ink tracking-wide">
                  {l.name}
                </h3>
                <p className="mt-4 text-desert-charcoal/75 leading-relaxed">
                  {l.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Selected Work */}
      <section id="work" className="py-24 md:py-32 bg-desert-ink text-desert-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="text-xs uppercase tracking-[0.25em] text-desert-stone mb-14">
            Selected Work
          </p>
          <div className="space-y-20 md:space-y-28">
            {selectedProjects.map((project, i) => (
              <article
                key={project.id}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center ${
                  i % 2 === 1 ? "" : ""
                }`}
              >
                <div
                  className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-2" : ""}`}
                >
                  <img
                    src={project.coverImage}
                    alt={project.coverAlt}
                    loading="lazy"
                    width={1024}
                    height={768}
                    className="w-full aspect-[4/3] object-cover"
                  />
                </div>
                <div
                  className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1" : ""}`}
                >
                  <p className="text-xs uppercase tracking-[0.25em] text-desert-stone mb-4">
                    {project.neighborhood}
                  </p>
                  <h3 className="font-display text-2xl md:text-3xl">
                    {project.name}
                  </h3>
                  <p className="mt-6 text-desert-cream/75 leading-relaxed">
                    {project.thesis}
                  </p>
                  <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
                    {project.services.map((s) => (
                      <span
                        key={s}
                        className="text-xs uppercase tracking-[0.15em] text-desert-cream/60 border-b border-desert-cream/25 pb-1"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                  {project.investmentRange && (
                    <p className="mt-6 text-xs uppercase tracking-[0.2em] text-desert-stone">
                      Investment — {project.investmentRange}
                    </p>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Consultation Request */}
      <section id="consultation" className="py-24 md:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl mb-16">
            <p className="text-xs uppercase tracking-[0.25em] text-desert-olive-deep mb-6">
              Begin
            </p>
            <h2 className="font-display text-3xl md:text-5xl leading-[1.15] text-desert-ink">
              Request a Property Consultation.
            </h2>
            <p className="mt-6 text-desert-charcoal/75 leading-relaxed">
              Tell us about the property and what isn't working. We take on a
              limited number of projects each season.
            </p>
          </div>
          <ConsultationForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-desert-stone/40 py-14">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <p className="text-sm font-medium uppercase tracking-[0.25em] text-desert-ink">
              Plant Autopilot
            </p>
            <p className="mt-3 text-sm text-desert-charcoal/60">
              Exterior design and private landscape stewardship.
              <br />
              Palm Springs, California.
            </p>
          </div>
          <div className="text-sm text-desert-charcoal/60 md:text-right">
            <p>
              <a
                href="mailto:hello@plantautopilot.com"
                className="hover:text-desert-olive-deep transition-colors"
              >
                hello@plantautopilot.com
              </a>
            </p>
            <p className="mt-1">
              <a
                href="tel:+17605550184"
                className="hover:text-desert-olive-deep transition-colors"
              >
                (760) 555-0184
              </a>
            </p>
            <p className="mt-4 text-xs text-desert-stone">
              © {new Date().getFullYear()} Plant Autopilot
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
